import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CommentIcon from '@mui/icons-material/Comment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIcon from '@mui/icons-material/Phone';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface FeedbackItem {
  id: string;
  user_id: string;
  user_email: string;
  feedback_text: string;
  phone_number?: string;
  status: 'new' | 'acknowledged' | 'resolved';
  created_at: string;
  updated_at: string;
}

export default function FeedbackPage() {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: '500',
        offset: '0'
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await apiClient.get<FeedbackItem[]>(`/api/feedback/admin?${params}`);
      setFeedback(response);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [statusFilter]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (feedbackId: string, newStatus: string) => {
    try {
      await apiClient.patch(`/api/feedback/admin/${feedbackId}`, {
        status: newStatus
      });
      
      // Update local state
      setFeedback(prev => prev.map(item => 
        item.id === feedbackId ? { ...item, status: newStatus as FeedbackItem['status'] } : item
      ));
    } catch (err) {
      console.error('Error updating feedback status:', err);
      setError('Failed to update status');
    }
  };

  const handleViewDetails = (item: FeedbackItem) => {
    setSelectedFeedback(item);
    setDetailsOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'error';
      case 'acknowledged':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-NZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && feedback.length === 0) {
    return <LoadingScreen message="Loading feedback..." />;
  }

  const paginatedFeedback = feedback.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CommentIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Feedback Management
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={fetchFeedback} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedFeedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No feedback found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFeedback.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.user_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.feedback_text}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.phone_number && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PhoneIcon fontSize="small" />
                          <Typography variant="body2">{item.phone_number}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.status}
                        size="small"
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="new">
                          <Chip label="New" color="error" size="small" />
                        </MenuItem>
                        <MenuItem value="acknowledged">
                          <Chip label="Acknowledged" color="warning" size="small" />
                        </MenuItem>
                        <MenuItem value="resolved">
                          <Chip label="Resolved" color="success" size="small" />
                        </MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleViewDetails(item)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={feedback.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Feedback Details</DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ pt: 2 }}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted By
                </Typography>
                <Typography variant="body1">{selectedFeedback.user_email}</Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted At
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedFeedback.created_at)}
                </Typography>
              </Box>

              {selectedFeedback.phone_number && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Phone
                  </Typography>
                  <Typography variant="body1">{selectedFeedback.phone_number}</Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label={selectedFeedback.status} 
                  color={getStatusColor(selectedFeedback.status)} 
                  size="small" 
                />
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Feedback
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  value={selectedFeedback.feedback_text}
                  InputProps={{ readOnly: true }}
                  rows={6}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
}